'use client'

import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'

import {
	getOrderFlowStepDefinitions,
	ORDER_FLOW_STEPS,
	type OrderFlowStep
} from '../../_constants/order-flow-steps.const'
import { useOrderFlow } from '../../_context/order-flow-context'
import {
	canReachOrderFlowStep,
	type OrderFlowStepReachabilityContext
} from '../../_utils/order-flow-step-guards'

import { AOrderProgressDesktop } from './_components/a-order-progress-desktop'
import { AOrderProgressMobile } from './_components/a-order-progress-mobile'
import { ORDER_FLOW_MOBILE_HEADER_EXTRA_ELEMENT_ID } from './_const/order-progress-bar.const'

type AOrderProgressBarProps = {
	/** Extra Tailwind classes applied to the root `<div>`. */
	className?: string
}

/**
 * Horizontal step indicator for the standard order flow.
 *
 * Each step renders as a labeled button; clicking navigates to that step via
 * `goToStep` from the order-flow context. A step is only clickable once the
 * user has reached it (`step <= maxStepReached`). Visual states:
 *
 * - **current / past** – solid dark purple bar (user is on or has passed this step)
 * - **visited future** – lighter purple bar (user has been here but navigated back)
 * - **locked** – grey bar (user has not yet reached this step)
 *
 * On small screens, only the active step label is shown with prev/next controls
 * (first step: spacer left; last step: spacer right) that respect `maxStepReached`
 * and the same guards as `goToStep`. On large screens, the same prev/next arrows sit outside the
 * labeled step row. The mobile cart snippet under the label is not expandable.
 *
 * Mobile UI is portaled into the header element {@link ORDER_FLOW_MOBILE_HEADER_EXTRA_ELEMENT_ID}.
 */
export function AOrderProgressBar({ className }: AOrderProgressBarProps) {
	const { currentStep, maxStepReached, goToStep, flowMode, selectedAmbulanceSlug, cartProducts } =
		useOrderFlow()

	const [mobileSlot, setMobileSlot] = useState<Element | null>(null)

	useEffect(() => {
		setMobileSlot(document.getElementById(ORDER_FLOW_MOBILE_HEADER_EXTRA_ELEMENT_ID))
	}, [])

	const stepDefs = useMemo(() => getOrderFlowStepDefinitions(flowMode), [flowMode])
	const stepCount = stepDefs.length
	const isSingleStep = stepCount === 1

	const activeDef = stepDefs.find(d => d.step === currentStep) ?? stepDefs[0]

	const prevStep = (currentStep - 1) as OrderFlowStep
	const nextStep = (currentStep + 1) as OrderFlowStep

	const reachability: OrderFlowStepReachabilityContext = useMemo(
		() => ({
			flowMode,
			selectedAmbulanceSlug,
			cartProductsLength: cartProducts.length
		}),
		[flowMode, selectedAmbulanceSlug, cartProducts.length]
	)

	const canGoBack =
		!isSingleStep &&
		currentStep > ORDER_FLOW_STEPS.SELECT_AMBULANCE &&
		prevStep <= maxStepReached &&
		canReachOrderFlowStep(prevStep, reachability)

	const canGoForward =
		!isSingleStep &&
		currentStep < ORDER_FLOW_STEPS.PERSONAL_DETAILS &&
		nextStep <= maxStepReached &&
		canReachOrderFlowStep(nextStep, reachability)

	const showBackArrow = !isSingleStep && currentStep > ORDER_FLOW_STEPS.SELECT_AMBULANCE
	const showForwardArrow = !isSingleStep && currentStep < ORDER_FLOW_STEPS.PERSONAL_DETAILS

	const mobileContent = (
		<AOrderProgressMobile
			stepDefs={stepDefs}
			stepCount={stepCount}
			isSingleStep={isSingleStep}
			activeDef={activeDef}
			currentStep={currentStep}
			maxStepReached={maxStepReached}
			prevStep={prevStep}
			nextStep={nextStep}
			canGoBack={canGoBack}
			canGoForward={canGoForward}
			showBackArrow={showBackArrow}
			showForwardArrow={showForwardArrow}
			goToStep={goToStep}
			reachability={reachability}
		/>
	)

	return (
		<>
			{mobileSlot && createPortal(mobileContent, mobileSlot)}

			{!isSingleStep && (
				<AOrderProgressDesktop
					className={className}
					stepDefs={stepDefs}
					currentStep={currentStep}
					maxStepReached={maxStepReached}
					prevStep={prevStep}
					nextStep={nextStep}
					canGoBack={canGoBack}
					canGoForward={canGoForward}
					showBackArrow={showBackArrow}
					showForwardArrow={showForwardArrow}
					goToStep={goToStep}
					reachability={reachability}
				/>
			)}
		</>
	)
}
